import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  Stack,
  Grid2,
  CircularProgress,
  Button,
  Autocomplete,
  TextField,
  Select,
  MenuItem,
  InputAdornment,
} from "@mui/material";
import { Iconify } from '../Minimal/src/components/iconify';
import Header from '../Header';
import Footer from '../Footer';
import CommonDatePicker from '../datepicker/CommonDatePicker';
import config from "../common/Config";
import * as XLSX from 'xlsx';
import { encryptPayload, normalizeEncryptedApiResponse } from "../../utils/customerCrypto";
import { getCompanySettings, resolveCustomerApiEncrypt } from "../../utils/companySettingsCache";

const MisProductReport = () => {
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const defaultReportrange =
    format(startOfMonth, 'dd/MM/yyyy') + ' - ' + format(endOfMonth, 'dd/MM/yyyy');
  const [dates, setDates] = useState([startOfMonth, endOfMonth]);
  const [reportrange, setReportrange] = useState(defaultReportrange);
  const [reportAmount, setReportAmount] = useState('');
  const [pageCndDataOptions, setPageCndDataOptions] = useState([]);
  const [pageCndData, setPageCndData] = useState('product_wise');
  const [showProductWise, setShowProductWise] = useState(true);
  const [productWiseOptions, setProductWiseOptions] = useState([]);
  const [productWise, setProductWise] = useState('');
  const [productInputValue, setProductInputValue] = useState('');
  const [productFilteredOptions, setProductFilteredOptions] = useState([]);
  const [productValue, setProductValue] = useState(null);
  const [productReport, setProductReport] = useState({ type: '', data: [] });
  const [showpickListOption, setShowpickListOption] = useState(false);
  const [pickListOptions, setPickListOptions] = useState([]);
  const [pickListWise, setPickListWise] = useState('');
  const [showSalesRepOption, setShowSalesRepOption] = useState(false);
  const [salesRepOptions, setSalesRepOptions] = useState([]);
  const [salesRepWise, setSalesRepWise] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const accessToken = localStorage.getItem('accessToken');
  const token = localStorage.getItem('token');

  const handleDateChange = (value) => {
    setDates(value);
    const startDate = format(value[0], 'dd/MM/yyyy');
    const endDate = format(value[1], 'dd/MM/yyyy');
    setReportrange(startDate + ' - ' + endDate);
    setPage(0);
  };

  const productHandleInputChange = (event, newInputValue) => {
    setProductInputValue(newInputValue);
    const newFilteredOptions = productWiseOptions.filter(option =>
      option.label.toLowerCase().includes(newInputValue.toLowerCase()) ||
      (option.code && option.code.toLowerCase().includes(newInputValue.toLowerCase()))
    );
    setProductFilteredOptions(newFilteredOptions);
  };

  const productHandleChange = (event, newValue) => {
    if (newValue) {
      setProductValue(newValue);
      setProductWise(newValue.value);
    } else {
      setProductValue(null);
      setProductWise('');
    }
  };

  const handleChangePickOption = (event) => {
    setPickListWise(event.target.value);
  };

  const handleChangeSRapOption = (event) => {
    setSalesRepWise(event.target.value);
  };

  const fetchProductOptions = useCallback(async () => {
    if (!dates[0] || !dates[1]) return;

    const startDate = format(dates[0], 'dd/MM/yyyy');
    const endDate = format(dates[1], 'dd/MM/yyyy');
    const pdtrange = startDate + ' - ' + endDate;
    const pdtData = new FormData();
    pdtData.append('reportrange', pdtrange);

    const apiPdtOptionUrl = `${config.API_URL}/purchases/getProducts`;
    try {
      const response = await fetch(apiPdtOptionUrl, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
        body: pdtData,
      });
      const data = await response.json();
      const formattedOptions = data.products.map(item => ({
        label: `${item.name} (${item.code})`,
        value: item.id,
        code: item.code,
        name: item.name
      }));

      setProductWiseOptions(formattedOptions);
      setProductFilteredOptions(formattedOptions);
    } catch (error) {
    }
  }, [accessToken, dates]);

  const fetchPickListOptions = useCallback(async () => {
    if (!dates[0] || !dates[1]) return;

    const startDate = format(dates[0], 'dd/MM/yyyy');
    const endDate = format(dates[1], 'dd/MM/yyyy');
    const pdtrange = startDate + ' - ' + endDate;
    const pdtData = new FormData();
    pdtData.append('reportrange', pdtrange);

    const apiPickListUrl = `${config.API_URL}/getpicklist`;
    try {
      const response = await fetch(apiPickListUrl, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: pdtData,
      });

      const data = await response.json();
      const formattedOptions = data.picklist.map(item => ({
        label: item.picklist_number,
        value: item.picklist_number,
      }));
      setPickListOptions(formattedOptions);
    } catch (error) {
    }
  }, [accessToken, dates]);

  const fetchSalesRepOptions = useCallback(async () => {
    if (!dates[0] || !dates[1]) return;

    const startDate = format(dates[0], 'dd/MM/yyyy');
    const endDate = format(dates[1], 'dd/MM/yyyy');
    const pdtrange = startDate + ' - ' + endDate;
    const pdtData = new FormData();
    pdtData.append('reportrange', pdtrange);

    const apiPickListUrl = `${config.API_URL}/getSalesRep`;
    try {
      const settings = await getCompanySettings();
      const useEncrypt = resolveCustomerApiEncrypt(settings);
      const payload = { reportrange: pdtrange };
      let body = pdtData;
      if (useEncrypt) {
        const ep = encryptPayload(payload);
        if (ep) {
          body = new FormData();
          body.append('encrypted_payload', ep);
        }
      }
      const response = await fetch(apiPickListUrl, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
        body,
      });

      const raw = await response.json();
      const data = normalizeEncryptedApiResponse(raw, useEncrypt) ?? raw;
      const formattedOptions = data.data.map(item => ({
        label: `${item.first_name} ${item.last_name}`,
        value: item.id,
      }));
      setSalesRepOptions(formattedOptions);
    } catch (error) {
    }
  }, [accessToken, dates]);

  const fetchProductWiseData = useCallback(async () => {
    if (!reportrange) return;

    setLoading(true);
    const settings = await getCompanySettings();
    const useEncrypt = resolveCustomerApiEncrypt(settings);
    const pdtData = new FormData();
    const ptype = pageCndData || 'product_wise';

    if (ptype === 'order_wise') {
      setProductWise('');
      setPickListWise('');
      setSalesRepWise('');
    } else if (ptype === 'pick_list_wise') {
      setProductWise('');
      setSalesRepWise('');
    } else if (ptype === 'sales_rep_wise') {
      setPickListWise('');
      setProductWise('');
    } else if (ptype === 'reject_order_wise' || ptype === 'undeliver_order_wise' || ptype === 'is_deleted_product_wise') {
      setProductWise('');
      setPickListWise('');
      setSalesRepWise('');
    }

    const plain = {
      draw: String(page + 1),
      start: String(page * rowsPerPage),
      length: String(rowsPerPage),
      reportrange,
      type: ptype,
      product_id: productWise,
      pick_list: pickListWise,
      sales_rep_id: salesRepWise,
    };
    if (useEncrypt) {
      const ep = encryptPayload(plain);
      if (ep) {
        pdtData.append('encrypted_payload', ep);
      } else {
        Object.entries(plain).forEach(([k, v]) => pdtData.append(k, v ?? ''));
      }
    } else {
      Object.entries(plain).forEach(([k, v]) => pdtData.append(k, v ?? ''));
    }

    const apiMissingProductUrl = `${config.API_URL}/get_Mis_Product_report`;

    try {
      const response = await fetch(apiMissingProductUrl, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: pdtData,
      });

      if (!response.ok) {
        throw new Error('Failed to fetch report');
      }
      const raw = await response.json();
      const data = normalizeEncryptedApiResponse(raw, useEncrypt) ?? raw;
      const reportData = { ...data, type: ptype };
      setProductReport(reportData);
      setTotalCount(data.recordsTotal || data.recordsFiltered || (data.data?.length || 0));

      if (ptype === 'reject_order_wise') {
        setReportAmount(data.order_total + ' | Reject Amt : ' + data.missing_total);
      } else if (ptype === 'undeliver_order_wise') {
        setReportAmount(data.order_total + ' | Undeliver Amt : ' + data.missing_total);
      } else if (ptype === 'is_deleted_product_wise') {
        setReportAmount(data.order_total + ' | Deleted Amt : ' + data.missing_total);
      } else {
        setReportAmount(data.order_total + ' | Missing Amt : ' + data.missing_total);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [accessToken, token, page, rowsPerPage, reportrange, pageCndData, productWise, pickListWise, salesRepWise]);

  useEffect(() => {
    if (accessToken === null) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }
    const options = [
      { value: 'product_wise', label: 'Product Wise' },
      { value: 'order_wise', label: 'Order Wise' },
      { value: 'pick_list_wise', label: 'Pick List Wise' },
    ];
    setPageCndDataOptions(options);
  }, [accessToken]);

  useEffect(() => {
    if (dates[0] && dates[1]) {
      fetchProductOptions();
      fetchPickListOptions();
      fetchSalesRepOptions();
    }
  }, [dates, fetchProductOptions, fetchPickListOptions, fetchSalesRepOptions]);

  useEffect(() => {
    if (reportrange) {
      const timer = setTimeout(() => {
        fetchProductWiseData();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [fetchProductWiseData]);

  useEffect(() => {
    setShowProductWise(false);
    setShowpickListOption(false);
    setShowSalesRepOption(false);

    switch (pageCndData) {
      case "product_wise":
        setShowProductWise(true);
        break;
      case "pick_list_wise":
        setShowpickListOption(true);
        break;
      case "sales_rep_wise":
        setShowSalesRepOption(true);
        break;
      default:
        break;
    }
    if (pageCndData === "") {
      setShowProductWise(true);
    }
  }, [pageCndData]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSubmit = () => {
    setPage(0);
    fetchProductWiseData();
  };

  const exportToExcel = async () => {
    const settings = await getCompanySettings();
    const useEncrypt = resolveCustomerApiEncrypt(settings);
    const formData = new FormData();
    const ptype = pageCndData || 'product_wise';
    const plain = {
      draw: '1',
      start: '0',
      length: totalCount,
      reportrange,
      type: ptype,
      product_id: productWise,
      pick_list: pickListWise,
      sales_rep_id: salesRepWise,
    };
    if (useEncrypt) {
      const ep = encryptPayload(plain);
      if (ep) {
        formData.append('encrypted_payload', ep);
      } else {
        Object.entries(plain).forEach(([k, v]) => formData.append(k, v ?? ''));
      }
    } else {
      Object.entries(plain).forEach(([k, v]) => formData.append(k, v ?? ''));
    }

    const apiMissingProductUrl = `${config.API_URL}/get_missing_order_report`;

    try {
      const response = await fetch(apiMissingProductUrl, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to fetch Products');
      }

      const raw = await response.json();
      const data = normalizeEncryptedApiResponse(raw, useEncrypt) ?? raw;
      const exportData = data.data.map(product => ({
        "Product Code": product.product_code || '',
        "Product Name": product.product_name || '',
        "Order Type": product.order_type || '',
        "Order Qty": product.order_qty || '',
        "Accept Qty": product.accept_qty || '',
        "Picking Qty": product.picking_qty || '',
        "Missing Qty": product.cross_total || '',
        "Deliver Qty": product.deliver_qty || '',
        "Undeliver Qty": product.undeliver_qty || '',
        "Reject/Deleted Qty": product.reject_deleted || ''
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'MisProductReport');

      const now = new Date();
      const formattedDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const formattedTime = `${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}-${String(now.getSeconds()).padStart(2, '0')}`;
      const filename = `cross-off-report-${formattedDate}-${formattedTime}.xlsx`;

      XLSX.writeFile(wb, filename);
    } catch (error) {
    }
  };

  const getHeadersForType = (type) => {
    switch (type) {
      case 'product_wise':
        return [
          { label: 'Sr. No', width: 70 },
          { label: 'Product (Code)', width: 250 },
          { label: 'Order Type', width: 120 },
          { label: 'Order Qty', width: 100 },
          { label: 'Accept Qty', width: 100 },
          { label: 'Missing Qty', width: 110 },
          { label: 'Deliver Qty', width: 110 },
          { label: 'Undeliver Qty', width: 130 },
          { label: 'Reject / Deleted Qty', width: 160 },
        ];
      case 'order_wise':
        return ['Sr. No', 'Order Date', 'Order Id', 'Customers (A/c No)', 'Order Amt', 'Missing Qty', 'Missing Amt', 'Delivered Amt', 'Return Amt', 'Status'];
      case 'pick_list_wise':
        return ['Sr. No', 'Order Date', 'Order Id', 'Customers (A/c No)', 'Order Amt', 'Missing Qty', 'Missing Amt', 'Delivered Amt', 'Return Amt', 'Status'];
      case 'sales_rep_wise':
        return ['Sr. No', 'Order Date', 'Order Id', 'Customers (A/c No)', 'Order Amt', 'Missing Qty', 'Missing Amt', 'Delivered Amt', 'Return Amt', 'Status'];
      case 'reject_order_wise':
        return ['Sr. No', 'Product (Code)', 'Order Type', 'Qty', 'Reject Amt'];
      case 'undeliver_order_wise':
        return ['Sr. No', 'Product (Code)', 'Order Type', 'Qty', 'Undeliver Amt'];
      case 'is_deleted_product_wise':
        return ['Sr. No', 'Product (Code)', 'Order Type', 'Qty', 'Deleted Amt'];
      default:
        return [];
    }
  };

  const renderTableRow = (row, index, type) => {
    switch (type) {
      case 'product_wise':
        return (
          <TableRow
            key={index}
            hover
            className="table-cell-style"
          >
            <TableCell >
              {page * rowsPerPage + index + 1}
            </TableCell>
            <TableCell >{row.product_name || ''}</TableCell>
            <TableCell >{row.order_type || ''}</TableCell>
            <TableCell align="right" >{row.order_qty || 0}</TableCell>
            <TableCell align="right" >{row.accept_qty || 0}</TableCell>
            <TableCell align="right" >{row.cross_total || 0}</TableCell>
            <TableCell align="right" >{row.deliver_qty || 0}</TableCell>
            <TableCell align="right" >{row.undeliver_qty || 0}</TableCell>
            <TableCell align="right" >{row.reject_deleted || 0}</TableCell>
          </TableRow>
        );
      case 'order_wise':
      case 'pick_list_wise':
      case 'sales_rep_wise':
        return (
          <TableRow
            key={index}
            hover
            className="table-cell-style"
          >
            <TableCell >
              {page * rowsPerPage + index + 1}
            </TableCell>
            <TableCell >{row.date || ''}</TableCell>
            <TableCell >{row.reference_no || ''}</TableCell>
            <TableCell >{row.customer || ''}</TableCell>
            <TableCell align="right" >{parseFloat(row.order_total || 0).toFixed(2)}</TableCell>
            <TableCell align="right" >{row.missing_total || 0}</TableCell>
            <TableCell align="right" >{row.return_total || 0}</TableCell>
            <TableCell align="right" >{row.deliverd_total || 0}</TableCell>
            <TableCell align="right" >{row.return_total || 0}</TableCell>
            <TableCell >{row.sale_status || ''}</TableCell>
          </TableRow>
        );
      case 'reject_order_wise':
        return (
          <TableRow
            key={index}
            hover
            className="table-cell-style"
          >
            <TableCell >
              {page * rowsPerPage + index + 1}
            </TableCell>
            <TableCell >{row.product_name || ''}</TableCell>
            <TableCell >{row.order_type || ''}</TableCell>
            <TableCell align="right" >{row.total_items || 0}</TableCell>
            <TableCell align="right" >{parseFloat(row.reject_total || 0).toFixed(2)}</TableCell>
          </TableRow>
        );
      case 'undeliver_order_wise':
        return (
          <TableRow
            key={index}
            hover
            className="table-cell-style"
          >
            <TableCell >
              {page * rowsPerPage + index + 1}
            </TableCell>
            <TableCell >{row.product_name || ''}</TableCell>
            <TableCell >{row.order_type || ''}</TableCell>
            <TableCell align="right" >{row.total_items || 0}</TableCell>
            <TableCell align="right" >{parseFloat(row.undeliver_total || 0).toFixed(2)}</TableCell>
          </TableRow>
        );
      case 'is_deleted_product_wise':
        return (
          <TableRow
            key={index}
            hover
            className="table-cell-style"
          >
            <TableCell >
              {page * rowsPerPage + index + 1}
            </TableCell>
            <TableCell >{row.product_name || ''}</TableCell>
            <TableCell >{row.order_type || ''}</TableCell>
            <TableCell align="right" >{row.total_items || 0}</TableCell>
            <TableCell align="right" >{parseFloat(row.deleted_total || 0).toFixed(2)}</TableCell>
          </TableRow>
        );
      default:
        return null;
    }
  };

  const renderFooter = (type) => {
    if (type === 'product_wise' && productReport) {
      return (
        <TableRow
          className="table-cell-style"
          sx={{
            "& .MuiTableCell-root": {
              fontWeight: 600,

            }
          }}
        >
          <TableCell colSpan={3} align="right" >
            Totals:
          </TableCell>
          <TableCell align="right" >
            {productReport.order_qty || 0}
          </TableCell>
          <TableCell align="right" >
            {productReport.accept_qty || 0}
          </TableCell>
          <TableCell align="right" >
            {productReport.cross_total || 0}
          </TableCell>
          <TableCell align="right" >
            {productReport.deliver_qty || 0}
          </TableCell>
          <TableCell align="right" >
            {productReport.undeliver_qty || 0}
          </TableCell>
          <TableCell align="right" >
            {productReport.reject_deleted || 0}
          </TableCell>
        </TableRow>
      );
    }
    return null;
  };

  const headers = getHeadersForType(productReport.type || 'product_wise');
  const data = productReport.data || [];

  return (
    <>
      {/* <Header /> */}
      <Box className="page-container">
        <Paper className="paper-style">
          <Box sx={{ px: 2, py: 1.2 }}>
            <Grid2 container spacing={1} display="flex" justifyContent="space-between" alignItems="center">
              <Grid2 size={{ xs: 12, md: 4}}>
                <Typography className="page-title">
                  MIS Product Report
                </Typography>
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                <CommonDatePicker
                  className="input-border"
                  value={dates}
                  showOneCalendar
                  onChange={handleDateChange}
                  format="dd/MM/yyyy"
                  character=" - "
                />
              </Grid2>

              {showProductWise && (
                <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                  <Autocomplete
                    className="input-border"
                    size="small"
                    options={productFilteredOptions}
                    value={productValue}
                    onChange={productHandleChange}
                    inputValue={productInputValue}
                    onInputChange={productHandleInputChange}
                    getOptionLabel={(option) => option.label || ''}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Box>
                          <Typography variant="body2">{option.name}</Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {option.code}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Select Product" />
                    )}
                  />
                </Grid2>
              )}

              {showpickListOption && (
                <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>

                  <Select
                    className="input-border"
                    fullWidth
                    size="small"
                    value={pickListWise}
                    onChange={handleChangePickOption}
                    displayEmpty
                  >
                    <MenuItem value="">Select Pick List</MenuItem>
                    {pickListOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid2>
              )}

              {showSalesRepOption && (
                <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                  <Select
                    className="input-border"
                    fullWidth
                    size="small"
                    value={salesRepWise}
                    onChange={handleChangeSRapOption}
                    displayEmpty
                  >
                    <MenuItem value="">Select Sales Rep</MenuItem>
                    {salesRepOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid2>
              )}

              <Grid2 size={{ xs: 12, sm: 6, md: 2 }} alignContent="end">
                <Stack direction="row" spacing={1}>
                  <Button
                    className="button-variant-contained"
                    variant="contained"
                    onClick={handleSubmit}
                    fullWidth
              
                  >
                    Submit
                  </Button>
                  <Button
                    variant="outlined"
                    className="button-variant-outlined"
                    onClick={exportToExcel}
                    startIcon={<Iconify icon="eva:download-outline" width={18} />}
                    fullWidth
                  >
                    Export
                  </Button>
                </Stack>
              </Grid2>
            </Grid2>

            {reportAmount && (
              <Box sx={{ my: 2, p: 2, backgroundColor: '#f5f7fa', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#212b36' }}>
                  Order Amt : {reportAmount}
                </Typography>
              </Box>
            )}
          </Box>

          {headers.length > 0 && (
            <TableContainer
            >
              <Table size="medium" className="table-cell-border" sx={{ tableLayout: "fixed" }}>
                <TableHead>
                  <TableRow className="table-cell-header">
                    {headers.map((header, idx) => {
                      const label = typeof header === 'string' ? header : (header?.label ?? '');
                      const width = typeof header === 'object' && header?.width ? header.width : 'auto';
                      const alignRight = idx > 0 && typeof label === 'string' && (label.includes('Qty') || label.includes('Amt'));
                      return (
                      <TableCell
                        key={idx}
                        align={alignRight ? 'right' : 'left'}
                        sx={{
                          width: width,
                          minWidth: width,
                        }}
                      >
                        {label}
                      </TableCell>
                    );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={headers.length} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : data.length > 0 ? (
                    data.map((row, index) => renderTableRow(row, index, productReport.type))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={headers.length} align="center" sx={{ py: 4, color: '#637381', fontSize: '0.75rem' }}>
                        No records found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
                {productReport.type === 'product_wise' && (
                  <TableFooter>
                    {renderFooter(productReport.type)}
                  </TableFooter>
                )}
              </Table>
            </TableContainer>
          )}

          {headers.length > 0 && (
            <Box display="flex" justifyContent="flex-end" alignItems="center">
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                labelRowsPerPage="Rows per page:"
                className="pagination-styles"
              />
            </Box>
          )}
        </Paper>
      </Box>
    </>
  );
};

export default MisProductReport;
